/**
 * **진영 선택**(Todo9). 새로 시작할 때(저장이 없거나 처음부터를 눌렀을 때) 어느 나라의 장수로 설지 고른다.
 *
 * 처음에는 고구려로 고정이었다. 영토전(세 나라가 땅을 다툰다)을 넣으며 백제·신라로도 설 수 있게 열었다. 나라가 정하는
 * 것은 병종(같은 골격에 가지별 보정, `troops.ts`), 만나는 동료, 그 나라 전용 장비다. 한 번 고르면 처음부터 다시 시작하기
 * 전까지 바뀌지 않는다 — 키운 병사와 동료가 그 나라 사람이다.
 *
 * 폰에서 잘못 눌러 바로 시작하지 않게 고르고 나서 확정한다(갈림길·전투 준비 화면과 같은 꼴).
 */
import { CULTURE_NAMES, Culture, TROOP_BY_ID, companionsOf } from '../sim/troops.ts';
import { dressLeader } from '../sim/scenario.ts';
import { button, clear, el } from './dom.ts';
import { drawUnitPortrait, iconKeyFor } from './unit-icon.ts';

interface CultureCard {
  readonly culture: Culture;
  readonly hanja: string;
  readonly strong: string;
  readonly weak: string;
  /** 그 나라만의 4티어 병종(기획서 6-2). */
  readonly symbol: string;
}

/** 기획서 6-2 「세 나라의 색」. 수치는 `troops.ts`의 가지별 보정이 정한다 — 여기는 그것을 한 줄로 옮긴 말이다. */
const CARDS: readonly CultureCard[] = [
  { culture: Culture.Goguryeo, hanja: '高句麗', strong: '기병이 가장 두껍고, 활(맥궁)이 멀리 나간다', weak: '도검 방어가 얇다', symbol: 'go_horse4' },
  { culture: Culture.Baekje, hanja: '百濟', strong: '활이 가장 아프고, 보병이 잘 버틴다', weak: '말이 약하다', symbol: 'bj_bow4' },
  { culture: Culture.Silla, hanja: '新羅', strong: '창이 길고 무겁고, 도검병이 늦게 무너진다', weak: '활이 약하다', symbol: 'sl_spear4' },
];

export function renderCulturePick(root: HTMLElement, onDone: (culture: Culture) => void): void {
  let picked: Culture | null = null;

  const list = el('div', { class: 'pick-list culture-list', role: 'group', 'aria-label': '나라' });
  const count = el('small', { 'aria-live': 'polite' });
  const go = button('', () => {
    if (picked !== null) onDone(picked);
  }, 'danger');
  go.append('이 나라로 시작한다', count);

  const cards = CARDS.map((c) => {
    const card = button('', () => {
      picked = c.culture;
      sync();
    }, 'pick-card culture-card');
    card.dataset.culture = String(c.culture);
    const portrait = el('canvas', { class: 'culture-portrait', width: '0', height: '0', 'aria-hidden': 'true' }) as HTMLCanvasElement;
    // 맨몸 장수(착장 없이) — 시작할 때의 모습이다. 굽기 전·못 받으면 빈 자리.
    void drawUnitPortrait(portrait, iconKeyFor(dressLeader(c.culture, {}, 1)), 2);
    const mates = companionsOf(c.culture).map((d) => d.name).join(' · ');
    card.append(
      portrait,
      el('b', {}, `${CULTURE_NAMES[c.culture]} ${c.hanja}`),
      el('span', { class: 'pick-men' }, `강한 곳 — ${c.strong}`),
      el('span', { class: 'pick-men' }, `약한 곳 — ${c.weak}`),
      el('span', { class: 'pick-lead' }, `상징 병종 ${TROOP_BY_ID.get(c.symbol)?.name ?? c.symbol} · 만날 부하장수 ${mates}`),
    );
    list.append(card);
    return { c, card };
  });

  const sync = (): void => {
    for (const { c, card } of cards) {
      const on = picked === c.culture;
      card.classList.toggle('on', on);
      card.setAttribute('aria-pressed', String(on));
    }
    count.textContent = picked === null ? '나라를 고르라' : CULTURE_NAMES[picked];
    go.disabled = picked === null;
  };
  sync();

  clear(root);
  root.append(
    el(
      'div',
      { class: 'result-wrap culture-pick' },
      el(
        'header',
        { class: 'result-head' },
        el('span', { class: 'eyebrow' }, 'ILDANGBAEK 일당백 · 새로 시작'),
        el('h1', {}, '어느 나라의 장수로 설까'),
      ),
      el(
        'section',
        { class: 'panel pick-note' },
        el('p', {}, '나라가 병사의 병종과 만날 부하장수, 쓸 수 있는 전용 장비를 정한다. 처음부터 다시 시작하기 전에는 바꿀 수 없다.'),
        el('p', { class: 'dim' }, '출정에서 만나는 적은 나머지 두 나라다.'),
      ),
      list,
      el('div', { class: 'result-actions' }, go),
    ),
  );
}
